'use client';

import React from 'react';
import { X, AlertCircle, HelpCircle, CheckCircle } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
  showCancel?: boolean;
}

export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'info',
  showCancel = true
}: ConfirmModalProps) {
  const overlayMouseDownTarget = React.useRef<EventTarget | null>(null);

  if (!isOpen) return null;

  const Icon = type === 'danger' || type === 'warning' ? AlertCircle : CheckCircle;
  const accentColor = type === 'danger' ? '#ef4444' : '#10b981';

  return (
    <div className="modal-overlay" 
      style={{ zIndex: 2000 }} 
      onMouseDown={(e) => overlayMouseDownTarget.current = e.target}
      onMouseUp={(e) => {
        if (e.target === e.currentTarget && overlayMouseDownTarget.current === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="modal-content matte high-density-form"
        style={{ 
          maxWidth: '440px', 
          width: '95%',
          padding: '1.25rem', 
          textAlign: 'center',
          position: 'relative',
          borderRadius: '24px',
          overflow: 'hidden'
        }}>
        <div style={{ 
          width: '56px', 
          height: '56px', 
          background: `${accentColor}15`, 
          color: accentColor, 
          borderRadius: '16px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          margin: '0 auto 1rem auto'
        }}>
          <Icon size={28} />
        </div>
        
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 0.5rem 0' }}>{title}</h2>
        <p style={{ color: 'var(--glass-60)', lineHeight: '1.5', marginBottom: '1.5rem', fontSize: '0.85rem' }}>
          {message}
        </p>

        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {showCancel && (
            <button 
              className="btn btn-secondary" 
              style={{ 
                flex: 1, 
                height: '40px',
                fontSize: '0.85rem'
              }} 
              onClick={onClose}
            >
              {cancelText}
            </button>
          )}
          <button 
            className="btn btn-primary" 
            style={{ 
              flex: 1,
              height: '40px',
              background: type === 'danger' ? 'var(--error)' : 'var(--accent)',
              fontSize: '0.85rem'
            }} 
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {type === 'success' || type === 'info' ? 'Got it' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
